import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useCalls } from "@/CallsContext";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { currencySymbol, formatTime, hms, minutesLabel, money, summarize } from "@/lib/callStats";

export default function HistoryPage() {
  const { visible, error, loading, period, refreshCalls } = useCalls();
  const symbol = currencySymbol(visible);
  const stats = summarize(visible);
  const [pending, setPending] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  async function confirmDelete() {
    if (!pending?.id || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/calls/${encodeURIComponent(pending.id)}`);
      await refreshCalls();
      setPending(null);
    } catch (err) {
      setDeleteError(apiErrorMessage(err) || "Could not delete this call. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="mpd-page">
      <div className="mpd-page-head">
        <div>
          <p className="mpd-kicker">History</p>
          <h1>Call log</h1>
          <p className="mpd-sub">
            {stats.calls} {stats.calls === 1 ? "call" : "calls"} · {minutesLabel(stats.minutes)} min · {money(stats.earnings, symbol)} estimated · avg {stats.calls ? hms(stats.avgSec) : "—"} · {period.label}
          </p>
        </div>
      </div>
      {error ? <p className="mp-error">{error}</p> : null}
      {deleteError && !pending ? <p className="mp-error">{deleteError}</p> : null}
      {loading ? <p className="mpd-muted">Loading calls…</p> : null}
      <div className="mp-table-wrap mpd-table-card">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Start</th>
              <th>End</th>
              <th>Minutes</th>
              <th>Duration</th>
              <th>Est. earnings</th>
              <th>Company</th>
              <th className="mp-table-action"> </th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={8}><p className="mp-empty">No synced calls for this period.</p></td>
              </tr>
            ) : (
              visible
                .slice()
                .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))
                .map((call) => (
                  <tr key={call.id}>
                    <td>{call.date}</td>
                    <td>{formatTime(call.startTime)}</td>
                    <td>{formatTime(call.endTime)}</td>
                    <td>{minutesLabel(call.durationMin ?? (call.durationSec || 0) / 60)}</td>
                    <td>{hms(call.durationSec)}</td>
                    <td>{money(call.earnings, call.currencySymbol || symbol)}</td>
                    <td>{call.companyName || call.platform || "—"}</td>
                    <td className="mp-table-action">
                      <button
                        type="button"
                        className="mp-call-delete"
                        aria-label="Delete call"
                        title="Delete call"
                        onClick={() => {
                          setDeleteError("");
                          setPending(call);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
      {pending ? (
        <div className="mpd-trial-overlay" role="dialog" aria-modal="true" aria-labelledby="mp-delete-call-title">
          <div className="mpd-trial-dialog">
            <h2 id="mp-delete-call-title">Delete this call?</h2>
            <p>This call will be removed from your history and your earnings and statistics will be recalculated.</p>
            {deleteError ? <p className="mp-error">{deleteError}</p> : null}
            <div className="mp-delete-call-actions">
              <Button
                type="button"
                variant="outline"
                disabled={deleting}
                onClick={() => {
                  if (deleting) return;
                  setPending(null);
                  setDeleteError("");
                }}
              >
                Cancel
              </Button>
              <Button type="button" variant="destructive" disabled={deleting} onClick={confirmDelete}>
                {deleting ? "Deleting…" : "Delete Call"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
